import styled, { keyframes } from 'styled-components';

import BottomSheet from '~/components/BottomSheet';
import Button from '~/components/Button';
import { headerHeight } from '~/components/layout/Header/Header.styles';
import { ThemeProp } from '~/theme/Themes';

// =============================================================================
// Sidebar shell
// =============================================================================

interface SidebarWrapperProps {
  $isOpen: boolean;
  $width: number;
  $isResizing: boolean;
}

export const SidebarWrapper = styled.div<SidebarWrapperProps>`
  /* Fixed from top of viewport, full 100vh */
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  overflow: hidden;
  width: ${(props: SidebarWrapperProps) => (props.$isOpen ? `${props.$width}px` : '0')};
  transition: ${(props: SidebarWrapperProps) => (props.$isResizing ? 'none' : 'width 0.3s ease')};
  border-left: ${(props: ThemeProp & SidebarWrapperProps) =>
    props.$isOpen ? `1px solid ${props.theme.colors.neutrals.c100}` : 'none'};
`;

export const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0.375rem;
  cursor: col-resize;
  z-index: 10;
  background: transparent;
  transition: background-color 0.15s;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 0.1875rem;
    height: 2.5rem;
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
    border-radius: 0.25rem;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.primary.c050};

    &::after {
      opacity: 1;
    }
  }
`;

interface SidebarInnerProps {
  $width: number;
}

export const SidebarInner = styled.div<SidebarInnerProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  min-width: ${(props: SidebarInnerProps) => (props.$width > 0 ? `${props.$width}px` : 'unset')};
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: calc(${headerHeight} + 1px);
  padding: 1rem 1.25rem 0.75rem 1.25rem;
  flex-shrink: 0;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: auto;
    padding: 1rem 1.25rem;
  }
`;

export const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
`;

export const SidebarTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  flex: 1;
`;

export const SidebarTitleIcon = styled.div`
  display: flex;
  align-items: center;

  svg {
    color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const SidebarTitleText = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  line-height: 1;
  font-size: 1.125rem;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

// =============================================================================
// Chat content
// =============================================================================

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
`;

export const MessagesList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 1.25rem;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  min-height: 0;

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
    border-radius: 0.25rem;

    &:hover {
      background: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
    }
  }
`;

interface MessageContainerProps {
  $isUser?: boolean;
}

export const MessageContainer = styled.div<MessageContainerProps>`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
  min-width: 0;
`;

export const MessageBubble = styled.div`
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 0.75rem;
  border-radius: ${(props: ThemeProp) => props.theme.borderRadius};
  background-color: ${(props: ThemeProp) => props.theme.colors.background};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const UserMessageBubble = styled(MessageBubble)`
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  border: none;
`;

export const MarkdownContent = styled.div`
  white-space: normal;

  p {
    margin: 0 0 0.75rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1rem 0 0.5rem 0;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
    line-height: 1.3;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 1.25rem;
  }

  h2 {
    font-size: 1.125rem;
  }

  h3 {
    font-size: 1rem;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
    font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  }

  ul {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
    font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  }

  code {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-family: 'SF Mono', Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.875em;
  }

  pre {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    padding: 0.75rem 1rem;
    border-radius: 0.375rem;
    overflow-x: hidden;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0.75rem 0;

    code {
      background-color: transparent;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid ${(props: ThemeProp) => props.theme.colors.primary.c300};
    margin: 0.75rem 0;
    padding-left: 1rem;
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c600};
  }

  a {
    color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    text-decoration: underline;

    &:hover {
      color: ${(props: ThemeProp) => props.theme.colors.primary.c600};
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    margin: 0.75rem 0;
    display: block;
    overflow-x: hidden;
  }

  th,
  td {
    border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
    padding: 0.5rem;
    text-align: left;
  }

  th {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  }

  hr {
    border: none;
    border-top: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
    margin: 1rem 0;
  }

  strong {
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  }
`;

export const InputContainer = styled.div`
  padding: 0.75rem 1.25rem 0.875rem;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  flex-shrink: 0;
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 1.5px solid transparent;
  border-radius: 0.75rem;
  background: linear-gradient(
        ${(props: ThemeProp) => props.theme.colors.neutrals.white},
        ${(props: ThemeProp) => props.theme.colors.neutrals.white}
      )
      padding-box,
    ${(props: ThemeProp) => props.theme.gradients.aiInsights} border-box;
  padding: 0.75rem 0.75rem 0.625rem 1rem;
  gap: 0.625rem;
  position: relative;
`;

export const TextareaWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  min-height: 3rem;
  position: relative;
  overflow: hidden;
`;

export const StyledTextarea = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  border-radius: 0;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  background: transparent;
  resize: none;
  min-height: 3rem;
  max-height: calc(${(props: ThemeProp) => props.theme.lineHeight.m} * 5);
  padding: 0;
  margin: 0;
  display: block;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  text-align: left;
  direction: ltr;
  scrollbar-width: none;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  }
`;

export const InputActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const AddAudienceButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  gap: 0.125rem;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  border-radius: 0.25rem;
  padding: 0 0.25rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: 1.3125rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  svg {
    width: 0.5rem;
    height: 0.5rem;
    flex-shrink: 0;
    stroke-width: 2;
    stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

export const ButtonWrapper = styled.div`
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SendButton = styled(Button)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  position: relative;
  padding: 0;
  height: 2rem;
  min-width: 2rem;
  width: 2rem;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.black} !important;
  border: none;
  box-shadow: none;

  svg {
    width: 1rem;
    height: 1rem;
    color: #fff;
    z-index: 3;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c800} !important;
  }

  &:hover:not(:disabled) {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c800};
  }
`;

const dotAnimation = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1.25rem;
  background-color: #fff;
  flex: 1;
  min-height: 100%;
  width: 100%;
`;

export const LoadingTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.l};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.l};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  text-align: center;
`;

export const LoadingSubtitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  text-align: center;
`;

export const LoadingDotsContainer = styled.div`
  display: flex;
  gap: 0.375rem;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`;

interface LoadingDotProps {
  $delay: number;
}

export const LoadingDot = styled.div<LoadingDotProps>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: #ff6b35;
  animation: ${dotAnimation} 1.4s infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

export const LoadingMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem;
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 100%;
  width: 100%;
  gap: 0.5rem;
  padding: 1.25rem;
`;

export const EmptyStateTitle = styled.div`
  text-align: center;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 2rem;
  background: ${(props: ThemeProp) => props.theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const EmptyStateSubtitle = styled.div`
  text-align: center;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  max-width: 16rem;
`;

export const SuggestedActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  flex-shrink: 0;
`;

export const SuggestedActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  width: 100%;
  border-radius: 0.375rem;
  background-color: #f5f9ff;
  color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
  border: none;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  cursor: pointer;
  transition: background-color 0.15s;
  text-align: left;
  min-width: 0;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.primary.c050};
  }
`;

export const SuggestedActionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const SuggestedActionIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    width: 0.875rem;
    height: 0.875rem;
    transform: scaleX(-1);
  }
`;

const toolStatusPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.75);
    opacity: 0.6;
  }
`;

export const ToolStatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
`;

export const ToolStatusDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: #ff6c0a;
  animation: ${toolStatusPulse} 1.4s ease-in-out infinite;
  flex-shrink: 0;
`;

interface ToolStatusTextProps {
  $visible?: boolean;
}

export const ToolStatusText = styled.span<ToolStatusTextProps>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  font-style: italic;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '6px')});
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
`;

export const PrivacyDisclaimer = styled.div`
  text-align: center;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
  padding: 0 1.25rem 1rem 1.25rem;
  flex-shrink: 0;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
`;

// =============================================================================
// Content push wrapper — used by OverviewPage to push content left on desktop
// when the sidebar is open (sidebar is position:fixed so it needs manual offset)
// =============================================================================

interface ContentPushProps {
  $sidebarWidth: number;
  $isOpen: boolean;
  $isResizing: boolean;
}

export const ContentPushWrapper = styled.div<ContentPushProps>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  /* On desktop and below the sidebar overlays (matching AppDrawer behavior), so no padding needed */
  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.desktop}) {
    padding-right: ${(props: ContentPushProps) => (props.$isOpen ? `${props.$sidebarWidth}px` : '0')};
    transition: ${(props: ContentPushProps) => (props.$isResizing ? 'none' : 'padding-right 0.3s ease')};
  }
`;

export const StyledBottomSheet = styled(BottomSheet)`
  /* Full-height sheet */
  [data-rsbs-overlay] {
    min-height: calc(100dvh - 3rem);
  }

  /* Let inner MessagesList own scrolling */
  [data-rsbs-scroll] {
    overflow: hidden;
    height: 100%;
  }

  /* Fill full height and remove default padding */
  [data-rsbs-content] {
    padding: 0;
    height: 100%;
  }
`;
